import React, {FC} from "react";
import {Button, Tooltip} from "antd";
import moment from "moment";
import {AttemptStepper} from "./AttemptStepper";
import {ActionBar, ActionBarAttemptShape} from "./ActionBar";
import {RefundList, RefundListItemShape} from "./RefundList";
import {HistoryExpander, CompactAttemptShape} from "./HistoryExpander";
import {deriveStepperState} from "./helpers/deriveStepperState";
import {getDisplayMollieStatus, getMollieFlowKind} from "./helpers/mollieFlowKind";
import {NON_TERMINAL_ATTEMPT_STATUSES} from "./helpers/derivePostenStatus";
import {formatMollieBanktransferLastValidDay} from "../../../../../shared/lib/mollieBanktransferDates";

const css = require("./MolliePaymentSection.less");

// Aufgeklapptes Detail einer Posten-Zeile (Sketch final-b, `tr.detail`).
//
// Wiederverwendete Bausteine: AttemptStepper, ActionBar (volle Variante),
// RefundList, HistoryExpander — letzterer NUR mit den früheren Attempts
// DIESES Postens.

export interface PostenDetailAttemptShape extends ActionBarAttemptShape {
    molliePaymentLinkId?: string | null;
    paidAt?: string | Date | null;
    createDate?: string | Date | null;
    errorMessage?: string | null;
}

export type PostenDetailProps = {
    scheduledPaymentId: string;
    bookingId: string;
    currentAttempt: PostenDetailAttemptShape | null;
    pastAttempts: ReadonlyArray<CompactAttemptShape>;
    refetchBooking: () => Promise<unknown>;
    isBookingCanceled: boolean;
    /** Person nach DSGVO-Löschantrag gelöscht; wird an die ActionBar durchgereicht. */
    attendeeErased?: boolean;
    isWaitlistBooking?: boolean;
    bookingFullySettled?: boolean;
    onOpenTimeline: (scheduledPaymentId: string) => void;
    /**
     * true, wenn `currentAttempt` der Sammel-Link-Attempt eines ANDEREN (Anker-)
     * Postens ist UND der Posten noch offen ist. Dann keine ActionBar: Link-
     * Aktionen laufen über die Anker-Zeile. Bezahlte Mitglieds-Posten bekommen
     * die ActionBar (Erstatten pro Rate) — der Aufrufer reicht dann `false`.
     */
    inBundle?: boolean;
    /**
     * false → keine ActionBar (alle Aktionen dort sind Admin-Mutationen; für
     * Kursleitungen bleiben nur Stepper, Stats, Refunds und Historie).
     */
    canManagePayments: boolean;
};

function formatDate(value: string | Date | null | undefined): string {
    if (!value) return "—";
    const parsed = moment(value);
    if (!parsed.isValid()) return "—";
    return parsed.format("L LT");
}

export const PostenDetail: FC<PostenDetailProps> = ({
    scheduledPaymentId,
    bookingId,
    currentAttempt,
    pastAttempts,
    refetchBooking,
    isBookingCanceled,
    attendeeErased,
    isWaitlistBooking,
    bookingFullySettled,
    onOpenTimeline,
    inBundle,
    canManagePayments,
}) => {
    const timelineButton = (
        <Button
            type="link"
            size="small"
            onClick={() => onOpenTimeline(scheduledPaymentId)}
            data-testid={`posten-timeline-link-${scheduledPaymentId}`}
        >
            {"Zahlungshistorie öffnen"}
        </Button>
    );

    if (!currentAttempt) {
        return (
            <div className={css.postenDetail} data-testid={`posten-detail-${scheduledPaymentId}`}>
                <div className={css.postenDetailHint}>
                    {"Für diesen Posten wurde noch kein Mollie-Zahlungslink erstellt."}
                </div>
                <div className={css.postenDetailActions}>{timelineButton}</div>
                <HistoryExpander
                    attempts={pastAttempts}
                    onAttemptClick={() => onOpenTimeline(scheduledPaymentId)}
                />
            </div>
        );
    }

    const flowKind = getMollieFlowKind(currentAttempt.mollieMethod);
    const rawDisplayStatus = getDisplayMollieStatus(
        currentAttempt.status,
        currentAttempt.mollieMethod,
        currentAttempt.linkExpiresAt,
    );
    // B-6967: bei stornierter Buchung nicht-terminale Status als "canceled"
    // darstellen — der Stepper darf keinen laufenden Versuch suggerieren.
    const displayStatus =
        isBookingCanceled && NON_TERMINAL_ATTEMPT_STATUSES.indexOf(rawDisplayStatus) !== -1
            ? "canceled"
            : rawDisplayStatus;
    const stepperState = deriveStepperState(
        {status: displayStatus, paidAt: currentAttempt.paidAt || null},
        bookingFullySettled,
        flowKind,
    );
    const stepperLabels =
        flowKind === "banktransfer"
            ? {
                created: "Zahlungsdaten erstellt",
                method: "Warten auf Überweisung",
                paid: "Zahlung eingegangen",
                settled: "Abgerechnet",
            }
            : undefined;
    // Nur die direkte Überweisung hat Überweisungsdaten mit eigener Gültigkeit; ein
    // Zahlungslink mit geerbter Methode banktransfer zeigt weiter den Link-Ablauf.
    // Gleiches Kriterium wie der Server (hydrateMollieAttemptExpiry).
    const isDirectBanktransfer = flowKind === "banktransfer" && !currentAttempt.molliePaymentLinkId;
    const expiryLabel = isDirectBanktransfer ? "Überweisungsdaten gültig bis" : "Link gültig bis";
    const refunds = (currentAttempt.refunds || []) as ReadonlyArray<RefundListItemShape>;
    const showRefundList = currentAttempt.status === "paid" && refunds.length > 0;

    return (
        <div className={css.postenDetail} data-testid={`posten-detail-${scheduledPaymentId}`}>
            <AttemptStepper state={stepperState} labels={stepperLabels} />

            <div className={css.timelineCardStats}>
                <div className={css.timelineCardStatItem}>
                    <span className={css.timelineCardStatLabel}>{"Erstellt"}</span>
                    <span className={css.timelineCardStatValue}>
                        {formatDate(currentAttempt.createDate)}
                    </span>
                </div>
                {currentAttempt.linkExpiresAt && (
                    <div className={css.timelineCardStatItem}>
                        <span className={css.timelineCardStatLabel}>{expiryLabel}</span>
                        <span
                            className={css.timelineCardStatValue}
                            data-testid="attempt-link-expires-at"
                        >
                            {isDirectBanktransfer
                                ? formatMollieBanktransferLastValidDay(currentAttempt.linkExpiresAt)
                                : formatDate(currentAttempt.linkExpiresAt)}
                        </span>
                    </div>
                )}
                {currentAttempt.paidAt && (
                    <div className={css.timelineCardStatItem}>
                        <span className={css.timelineCardStatLabel}>{"Bezahlt am"}</span>
                        <span className={css.timelineCardStatValue} data-testid="attempt-paid-at">
                            {formatDate(currentAttempt.paidAt)}
                        </span>
                    </div>
                )}
                <div className={css.timelineCardStatItem}>
                    <span className={css.timelineCardStatLabel}>{"Mollie-ID"}</span>
                    <Tooltip title={currentAttempt.molliePaymentId}>
                        <span className={css.timelineCardStatValue} data-testid="attempt-mollie-id">
                            {currentAttempt.molliePaymentId}
                        </span>
                    </Tooltip>
                </div>
            </div>

            {currentAttempt.errorMessage && (
                <div className={css.timelineCardErrorRow} data-testid="attempt-error-message">
                    {currentAttempt.errorMessage}
                </div>
            )}

            {/* Aktionen in EINER Zeile unter dem Stepper (statt rechter Spalte —
                dort brachen die Labels um). */}
            <div className={css.postenDetailActions}>
                {inBundle ? (
                    <span className={css.postenSubLine} data-testid="posten-detail-bundle-hint">
                        {"Dieser Posten ist Teil eines Sammel-Links — Aktionen über den Posten, der den Sammel-Link trägt."}
                    </span>
                ) : canManagePayments ? (
                    <ActionBar
                        attempt={currentAttempt}
                        bookingId={bookingId}
                        refetchBooking={refetchBooking}
                        isBookingCanceled={isBookingCanceled}
                        isWaitlistBooking={isWaitlistBooking}
                        attendeeErased={attendeeErased}
                        variant="full"
                    />
                ) : null}
                {timelineButton}
            </div>

            {showRefundList && <RefundList refunds={refunds} />}

            <HistoryExpander
                attempts={pastAttempts}
                onAttemptClick={() => onOpenTimeline(scheduledPaymentId)}
            />
        </div>
    );
};

export default PostenDetail;
